"use client";

import { useEffect, useRef, type ElementType, type ReactNode } from "react";

// TS infers `never` props for a bare ElementType in JSX; narrowing the rendered
// tag to a concrete HTML element type restores the shared attributes (ref,
// className, style, data-*) — which is all Reveal ever passes.
type HTMLTag = "div";

/**
 * Scroll reveal. Adds `.is-in` when the element first enters the viewport; the
 * transition itself lives in globals.css ([data-reveal]). Latches (fires once)
 * and no-ops under reduced motion because the CSS keeps it visible there.
 *
 * `delay` staggers siblings (ms). `as` lets it render any tag without an extra
 * wrapper where semantics matter.
 */
export function Reveal({
  children,
  as: Tag = "div",
  delay = 0,
  className,
}: {
  children: ReactNode;
  as?: ElementType;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const Component = Tag as HTMLTag;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          el.classList.add("is-in");
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Component
      ref={ref}
      data-reveal=""
      className={className}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Component>
  );
}
